
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AuthHeader } from '@/components/AuthHeader';
import { LoadingSpinner } from '@/components/LoadingSpinner';

interface AuthRedirectHandlerProps {
  children: React.ReactNode;
}

export const AuthRedirectHandler = ({ children }: AuthRedirectHandlerProps) => {
  const [redirecting, setRedirecting] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  // Check if this is an invitation flow
  const invitationId = searchParams.get('invitation');
  const isInvitationFlow = !!invitationId;

  // Check if this is a password reset flow - redirect to dedicated page
  const isPasswordResetFlow = searchParams.has('type') && searchParams.get('type') === 'recovery';

  // Redirect authenticated users to dashboard
  useEffect(() => {
    // Handle password reset flow - redirect to dedicated page
    if (isPasswordResetFlow && !redirecting) {
      setRedirecting(true);
      const params = new URLSearchParams(searchParams);
      navigate(`/auth/reset-password?${params.toString()}`, { replace: true });
      return;
    }

    // Skip redirect handling for invitation flows - let the invitation handler manage this
    if (isInvitationFlow) {
      return;
    }

    if (user && !authLoading && !redirecting) {
      setRedirecting(true);
      
      const redirectTo = searchParams.get('redirect');
      
      // Small delay to ensure smooth transition
      setTimeout(() => {
        navigate(redirectTo || '/dashboard', { replace: true });
      }, 100);
    }
  }, [user, authLoading, navigate, searchParams, isInvitationFlow, isPasswordResetFlow, redirecting]);

  // Show loading state while redirecting (but not for invitation flows)
  if (user && (authLoading || redirecting) && !isInvitationFlow) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header for authenticated users */}
        <header className="bg-white border-b border-gray-200 relative z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <h1 className="text-xl font-semibold">CampChef Pro</h1>
              </div>
              <AuthHeader />
            </div>
          </div>
        </header>
        
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <LoadingSpinner message="Redirecting..." />
        </div>
      </div>
    );
  }

  // Render children (auth forms) for non-authenticated users or invitation flows
  return <>{children}</>;
};
